/**
 * AcceptCollectionOfferModal - Modal for sellers to accept a collection offer
 *
 * Flow:
 * 1. Seller sees their NFTs from this collection
 * 2. Seller selects which NFT to sell
 * 3. Seller confirms and signs NFTokenCreateOffer via Xaman
 * 4. Backend executes the trade (EscrowFinish, AcceptOffer, CreateOffer for buyer)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { Avatar } from '../../ui/components/Avatar';
import { Badge } from '../../ui/components/Badge';
import { FeatherX } from '@subframe/core';
import {
  acceptCollectionOffer,
  executeCollectionOfferTrade,
  type CollectionOffer,
  type TraitFilters,
} from '../../api/collectionOffers';
import { RarityRank } from '../nft/RarityRank';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { formatPrice } from '../../utils/formatters';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import type { SigningPayload } from '../../types/signing';

interface NFTTrait {
  trait_type: string;
  value: string | number;
}

interface UserNFT {
  nftTokenId: string;
  name: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  rarityRank?: number;
  totalSupply?: number;
  traits?: NFTTrait[];
}

/**
 * Check if an NFT matches the offer's trait filters
 * Logic: OR within trait type, AND between trait types
 */
const matchesTraitFilter = (
  nftTraits: NFTTrait[] | null | undefined,
  traitFilters: TraitFilters | null | undefined
): boolean => {
  // No filters means any NFT matches
  if (!traitFilters || Object.keys(traitFilters).length === 0) {
    return true;
  }

  // No traits on NFT means it can't match any filter
  if (!nftTraits || nftTraits.length === 0) {
    return false;
  }

  // Create a lookup map for NFT traits
  const traitMap = new Map<string, Set<string>>();
  for (const trait of nftTraits) {
    const key = trait.trait_type.toLowerCase();
    if (!traitMap.has(key)) {
      traitMap.set(key, new Set());
    }
    traitMap.get(key)!.add(String(trait.value).toLowerCase());
  }

  // Check each trait type filter (AND between types)
  for (const [traitType, allowedValues] of Object.entries(traitFilters)) {
    const nftValues = traitMap.get(traitType.toLowerCase());

    if (!nftValues) {
      // NFT doesn't have this trait type at all
      return false;
    }

    // Check if any of the NFT's values for this trait match any allowed value (OR within type)
    const hasMatch = allowedValues.some(value =>
      nftValues.has(value.toLowerCase())
    );

    if (!hasMatch) {
      return false;
    }
  }

  return true;
};

interface AcceptCollectionOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: CollectionOffer;
  userNfts: UserNFT[];
  collectionName: string;
  onSuccess?: () => void;
}

type ModalStep = 'select_nft' | 'confirm' | 'signing' | 'executing' | 'success' | 'error';

export const AcceptCollectionOfferModal: React.FC<AcceptCollectionOfferModalProps> = ({
  isOpen,
  onClose,
  offer,
  userNfts,
  collectionName,
  onSuccess,
}) => {
  const { user } = useAuthStore();
  const { success: showSuccess } = useNotificationStore();

  const [step, setStep] = useState<ModalStep>('select_nft');
  const [selectedNft, setSelectedNft] = useState<UserNFT | null>(null);
  const [error, setError] = useState<string>('');

  // Normalized signing payload (any provider). When set, WalletSigningModal owns the
  // signing lifecycle (Xaman QR / deep link, or WalletConnect approval for joey-wc).
  const [signing, setSigning] = useState<SigningPayload | null>(null);
  // Guards against a double-click firing two escrow-signing payloads during the round-trip
  // (the signing modal only mounts after the request resolves).
  const [confirming, setConfirming] = useState(false);

  // Filter NFTs by trait filters
  const filteredNfts = useMemo(() => {
    return userNfts.filter(nft =>
      matchesTraitFilter(nft.traits, offer.traitFilters)
    );
  }, [userNfts, offer.traitFilters]);

  // Check if offer has trait filters
  const hasTraitFilters = offer.traitFilters && Object.keys(offer.traitFilters).length > 0;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // If only one matching NFT, skip selection
      if (filteredNfts.length === 1) {
        setSelectedNft(filteredNfts[0]);
        setStep('confirm');
      } else {
        setStep('select_nft');
        setSelectedNft(null);
      }
      setError('');
      setSigning(null);
    }
  }, [isOpen, filteredNfts]);

  // WalletSigningModal resolved the signing in our favor. Run the escrow follow-up
  // (executeCollectionOfferTrade) that completes the collection-offer trade.
  const handleSignedSuccess = async (txid: string) => {
    setSigning(null);
    setStep('executing');

    try {
      await executeCollectionOfferTrade({
        offerId: offer.id,
        sellerOfferTxHash: txid,
        nftTokenId: selectedNft!.nftTokenId,
        sellerAddress: user!.xrpl_address,
      });

      setStep('success');
      showSuccess(`Successfully sold ${selectedNft!.name} for ${formatPrice(offer.sellerReceivesXrp)}!`);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Trade execution failed');
      setStep('error');
    }
  };

  const handleRejected = () => {
    setSigning(null);
    setError('Transaction was rejected');
    setStep('error');
  };

  const handleExpired = () => {
    setSigning(null);
    setError('Transaction request expired');
    setStep('error');
  };

  const handleSelectNft = (nft: UserNFT) => {
    setSelectedNft(nft);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!selectedNft || !user?.xrpl_address || confirming) return;

    setError('');
    setConfirming(true);

    try {
      const result = await acceptCollectionOffer({
        offerId: offer.id,
        nftTokenId: selectedNft.nftTokenId,
        userAddress: user.xrpl_address,
      });

      setSigning(result.signing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create transaction');
      setStep('error');
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    if (step === 'success') {
      onSuccess?.();
    }
    onClose();
  };

  const getTitle = () => {
    switch (step) {
      case 'select_nft': return 'Select NFT to Sell';
      case 'confirm': return 'Confirm Sale';
      case 'signing': return 'Sign Transaction';
      case 'executing': return 'Processing Sale';
      case 'success': return 'Sale Complete!';
      case 'error': return 'Error';
      default: return 'Accept Offer';
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Content className="w-full max-w-[480px]">
        <div className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {getTitle()}
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Content */}
          {step === 'select_nft' && (
            <div className="flex flex-col gap-4 w-full">
              <p className="text-body text-subtext-color">
                Select which NFT from {collectionName} you want to sell for{' '}
                <span className="text-success-600 font-medium">
                  {formatPrice(offer.sellerReceivesXrp)}
                </span>
              </p>

              {/* Show required traits if the offer has trait filters */}
              {hasTraitFilters && (
                <div className="bg-neutral-50 rounded-md p-3">
                  <p className="text-caption font-medium text-default-font mb-2">
                    This offer requires specific traits:
                  </p>
                  <div className="flex flex-col gap-0.5">
                    {Object.entries(offer.traitFilters!).map(([traitType, values]) => (
                      <div key={traitType} className="text-caption">
                        <span className="text-subtext-color">{traitType}:</span>{' '}
                        <span className="text-default-font">{values.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {userNfts.length === 0 ? (
                <p className="text-body text-subtext-color text-center py-8">
                  You don't own any NFTs from this collection.
                </p>
              ) : filteredNfts.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="24" cy="24" r="23" fill="rgb(7, 48, 59)" stroke="rgb(7, 56, 68)" strokeWidth="2" />
                    <path
                      d="M24 14c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10-4.48-10-10-10zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-5h2v2h-2v-2zm0-8h2v6h-2v-6z"
                      fill="rgb(5, 162, 194)"
                    />
                  </svg>
                  <p className="text-body-bold text-default-font text-center">
                    No matching NFTs
                  </p>
                  <p className="text-body text-subtext-color text-center max-w-sm">
                    You own {userNfts.length} NFT{userNfts.length > 1 ? 's' : ''} from this collection, but none match the required traits for this offer.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
                  {filteredNfts.map((nft) => (
                    <div
                      key={nft.nftTokenId}
                      className="flex items-center gap-3 p-3 rounded-lg border border-neutral-border hover:border-brand-600 hover:bg-neutral-50 transition-colors"
                    >
                      <button
                        onClick={() => handleSelectNft(nft)}
                        className="flex items-center gap-3 flex-1 text-left min-w-0"
                      >
                        <Avatar
                          size="medium"
                          image={nft.thumbnailUrl || nft.imageUrl}
                        >
                          {nft.name?.charAt(0) || '?'}
                        </Avatar>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-body-bold text-default-font truncate">
                            {nft.name || 'Unnamed NFT'}
                          </span>
                          <div className="flex items-center gap-2 text-caption text-subtext-color">
                            {nft.rarityRank && nft.totalSupply && (
                              <RarityRank
                                rank={nft.rarityRank}
                                totalSupply={nft.totalSupply}
                                showTotal
                              />
                            )}
                            {nft.rarityRank && nft.totalSupply && <span>•</span>}
                            <span className="truncate">
                              {nft.nftTokenId.slice(0, 8)}...{nft.nftTokenId.slice(-8)}
                            </span>
                          </div>
                        </div>
                      </button>
                      <a
                        href={`/nfts/${nft.nftTokenId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-caption text-brand-600 hover:text-brand-700 underline whitespace-nowrap"
                      >
                        View NFT
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'confirm' && selectedNft && (
            <div className="flex flex-col gap-4 w-full">
              <div className="flex items-center gap-4 p-4 bg-neutral-50 rounded-lg">
                <Avatar
                  size="large"
                  image={selectedNft.thumbnailUrl || selectedNft.imageUrl}
                >
                  {selectedNft.name?.charAt(0) || '?'}
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-body-bold text-default-font">
                    {selectedNft.name || 'Unnamed NFT'}
                  </span>
                  <span className="text-caption text-subtext-color">
                    {collectionName}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">Offer Amount</span>
                  <span className="text-default-font">{formatPrice(offer.displayAmountXrp)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">Marketplace Fee (1%)</span>
                  <span className="text-default-font">
                    -{formatPrice(Math.round((offer.displayAmountXrp - offer.sellerReceivesXrp) * 100000) / 100000)}
                  </span>
                </div>
                <div className="flex justify-between text-body-bold border-t border-neutral-border pt-2">
                  <span className="text-default-font">You Receive</span>
                  <span className="text-success-600">{formatPrice(offer.sellerReceivesXrp)}</span>
                </div>
              </div>

              <div className="bg-neutral-50 rounded-md p-3">
                <p className="text-caption text-subtext-color">
                  <span className="font-semibold text-default-font">Accepting a Collection Offer:</span>{' '}
                  A buyer has escrowed {formatPrice(offer.displayAmountXrp)} to purchase any NFT from this collection.
                  The 1% marketplace fee applies to collection offer sales.
                </p>
              </div>

              <p className="text-caption text-subtext-color">
                You'll be asked to sign an NFT sell offer. Once signed, the trade will be executed automatically.
              </p>
            </div>
          )}

          {step === 'executing' && (
            <div className="flex flex-col items-center gap-4 py-8 w-full">
              <div className="w-12 h-12 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-body text-subtext-color text-center">
                Processing your sale...
              </p>
              <p className="text-caption text-subtext-color text-center">
                This may take a moment while the trade is executed on the XRPL.
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center gap-4 py-8 w-full">
              <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-body-bold text-default-font text-center">
                Sale Complete!
              </p>
              <p className="text-body text-subtext-color text-center">
                You received {formatPrice(offer.sellerReceivesXrp)} for {selectedNft?.name}
              </p>
            </div>
          )}

          {step === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="w-16 h-16 rounded-full bg-error-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-error-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-body text-error-600 text-center">
                {error || 'Something went wrong'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-3">
            {step === 'select_nft' && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                {filteredNfts.length === 0 ? 'Close' : 'Cancel'}
              </Button>
            )}

            {step === 'confirm' && (
              <>
                <Button variant="neutral-secondary" onClick={() => filteredNfts.length > 1 ? setStep('select_nft') : handleClose()}>
                  {filteredNfts.length > 1 ? 'Back' : 'Cancel'}
                </Button>
                <Button variant="brand-primary" onClick={handleConfirm} disabled={confirming} loading={confirming}>
                  Confirm Sale
                </Button>
              </>
            )}

            {(step === 'success' || step === 'error') && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog>

    {signing && (
      <WalletSigningModal
        open={true}
        onClose={() => setSigning(null)}
        title="Sign Transaction"
        payload={signing}
        onSuccess={handleSignedSuccess}
        onRejected={handleRejected}
        onExpired={handleExpired}
      />
    )}
    </>
  );
};
